import * as THREE from 'three';
import type { FaunaArchetype } from '../BiomeProfile';

/**
 * Low-poly creature builders. Bodies are composed from clean geometric
 * primitives — ellipsoids, tapered cylinders, soft cones — with carefully
 * authored proportions and a recognisable silhouette per archetype:
 *
 *   walker   — friendly quadruped grazer (alien deer/llama)
 *   crawler  — segmented slug/pod with antennae
 *   glider   — streamlined manta-ray with curved wings
 *   predator — lean six-legged hunter with a long whip tail
 *   spectre  — luminous floating orb with trailing tendrils
 *
 * Each species (named via `displayName`) layers archetype-specific accents
 * on top: ring segments, crystal plates, metal armor, glow vents, etc.
 *
 * Body forward is local +X. Legs swing around their local Z axis (the
 * side-to-side axis), so `leg.rotation.z` is the gait swing — animation
 * code in `Creature.ts` honours that.
 *
 * Each leg group carries `userData.gaitPhase` so the diagonal-trot
 * pattern (front-right + back-left vs. front-left + back-right) reads
 * correctly without the animation code needing to know leg layout.
 */

export interface CreatureColors {
  readonly bodyMat: THREE.MeshStandardMaterial;
  readonly accentMat: THREE.MeshStandardMaterial;
  readonly emissiveMat: THREE.MeshStandardMaterial | null;
}

export interface CreatureMesh {
  readonly root: THREE.Group;
  readonly parts: CreatureParts;
  /** Base body height in world units — used by the entity for the
   *  collision capsule and obstacle registration. */
  readonly bodyRadius: number;
  readonly bodyHeight: number;
}

export interface CreatureParts {
  readonly head?: THREE.Object3D;
  readonly body: THREE.Object3D;
  readonly legs: readonly THREE.Object3D[];
  readonly wings: readonly THREE.Object3D[];
  readonly tail?: THREE.Object3D;
  /** Body segments (crawler) — driven by undulating wave animation. */
  readonly segments?: readonly THREE.Object3D[];
}

export function buildCreature(
  archetype: FaunaArchetype,
  scale: number,
  colors: CreatureColors,
  speciesName: string
): CreatureMesh {
  const traits = traitsFor(speciesName);
  switch (archetype) {
    case 'walker':
      return buildWalker(scale, colors, traits);
    case 'crawler':
      return buildCrawler(scale, colors, traits);
    case 'glider':
      return buildGlider(scale, colors, traits);
    case 'predator':
      return buildPredator(scale, colors, traits);
    case 'spectre':
      return buildSpectre(scale, colors, traits);
  }
}

/* ─────────────────────── species traits ─────────────────────── */

interface SpeciesTraits {
  readonly rings: boolean;
  readonly dorsalGlow: boolean;
  readonly crystalPlates: boolean;
  readonly metalPlates: boolean;
  readonly crystalWings: boolean;
  readonly slimWings: boolean;
  readonly bulbAbdomen: boolean;
  readonly headFins: boolean;
  readonly heavyLegs: boolean;
  readonly trailingTendrils: boolean;
  readonly elongated: boolean;
  readonly glowPatches: boolean;
  readonly lengthMul: number;
  readonly seed: number;
}

function traitsFor(name: string): SpeciesTraits {
  const lower = name.toLowerCase();
  const has = (kw: string) => lower.includes(kw);
  return {
    rings: has('anel'),
    dorsalGlow: has('plasma') || has('térmico') || has('termico') || has('luminal'),
    crystalPlates: has('cristal') || has('gelo') || has('aurora'),
    metalPlates: has('ferroso') || has('metálico') || has('metalico') || has('vidro'),
    crystalWings: has('cristal'),
    slimWings: has('cortante'),
    bulbAbdomen: has('polinizador') || has('coletor de brilho'),
    headFins: has('nevoeiro'),
    heavyLegs: has('guardião') || has('guardiao'),
    trailingTendrils: has('flutuador') || has('espectro'),
    elongated: has('corredor') || has('silencioso'),
    glowPatches: has('toca') || has('luminal'),
    lengthMul: has('corredor') || has('silencioso') ? 1.25 : 1.0,
    seed: hashSeed(name)
  };
}

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 0xffffffff;
}

/* ─────────────────────── primitives ─────────────────────── */

/** Smooth ellipsoid built from an icosphere with per-axis scale baked
 *  into vertices — clean low-poly silhouette without the lumpy noise. */
function ellipsoid(
  radius: number,
  sx: number,
  sy: number,
  sz: number,
  material: THREE.Material,
  detail = 1
): THREE.Mesh {
  const geom = new THREE.IcosahedronGeometry(radius, detail);
  const pos = geom.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    pos.setXYZ(i, pos.getX(i) * sx, pos.getY(i) * sy, pos.getZ(i) * sz);
  }
  pos.needsUpdate = true;
  geom.computeVertexNormals();
  return new THREE.Mesh(geom, material);
}

function sphere(radius: number, material: THREE.Material, detail = 1): THREE.Mesh {
  return new THREE.Mesh(new THREE.IcosahedronGeometry(radius, detail), material);
}

function tapered(
  rTop: number,
  rBot: number,
  height: number,
  material: THREE.Material,
  segments = 8
): THREE.Mesh {
  return new THREE.Mesh(
    new THREE.CylinderGeometry(rTop, rBot, height, segments),
    material
  );
}

function cone(
  rBase: number,
  height: number,
  material: THREE.Material,
  segments = 6
): THREE.Mesh {
  return new THREE.Mesh(
    new THREE.ConeGeometry(rBase, height, segments),
    material
  );
}

/** Articulated leg: hip-pinned group with upper segment, knee group,
 *  lower segment and a flat-padded foot. Origin at the hip; the foot
 *  rests at -length world Y when the leg hangs straight. */
function buildLeg(
  length: number,
  hipR: number,
  footR: number,
  material: THREE.Material
): THREE.Group {
  const group = new THREE.Group();

  const upper = tapered(hipR, hipR * 0.78, length * 0.5, material, 8);
  upper.position.y = -length * 0.25;
  group.add(upper);

  const knee = new THREE.Group();
  knee.position.y = -length * 0.5;
  knee.rotation.x = 0.16;
  group.add(knee);

  const lower = tapered(hipR * 0.78, footR * 1.1, length * 0.5, material, 8);
  lower.position.y = -length * 0.25;
  knee.add(lower);

  const foot = ellipsoid(footR * 1.4, 1.2, 0.55, 1.5, material, 0);
  foot.position.y = -length * 0.5;
  knee.add(foot);

  return group;
}

/** Eye — small bright dot, optionally emissive. */
function buildEye(
  size: number,
  emissiveMat: THREE.MeshStandardMaterial | null,
  fallback: THREE.MeshStandardMaterial
): THREE.Mesh {
  const mat = emissiveMat ?? fallback;
  return new THREE.Mesh(new THREE.IcosahedronGeometry(size, 0), mat);
}

/** Pointed ear — a small cone tilted outward. */
function buildEar(size: number, material: THREE.Material): THREE.Mesh {
  return cone(size * 0.55, size * 1.2, material, 6);
}

/** Soft membrane wing — bent rectangular plane. */
function buildSoftWing(
  length: number,
  width: number,
  material: THREE.Material
): THREE.Mesh {
  const geom = new THREE.PlaneGeometry(width, length, 4, 14);
  const pos = geom.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const t = (y + length / 2) / length;
    const arc = Math.sin(t * Math.PI) * length * 0.18;
    pos.setZ(i, arc);
    const taper = 1 - Math.abs(t - 0.4) * 0.6;
    pos.setX(i, x * taper);
  }
  pos.needsUpdate = true;
  geom.computeVertexNormals();
  const mesh = new THREE.Mesh(geom, material);
  if (material instanceof THREE.MeshStandardMaterial) material.side = THREE.DoubleSide;
  return mesh;
}

/** Crystalline blade wing — diamond silhouette with a small ridge. */
function buildCrystalWing(
  length: number,
  width: number,
  material: THREE.Material
): THREE.Mesh {
  const geom = new THREE.PlaneGeometry(width, length, 3, 10);
  const pos = geom.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const t = (y + length / 2) / length;
    const diamond = Math.min(t * 2, 2 - t * 2);
    pos.setX(i, x * diamond);
    pos.setZ(i, Math.sin(t * Math.PI) * length * 0.08);
  }
  pos.needsUpdate = true;
  geom.computeVertexNormals();
  const mesh = new THREE.Mesh(geom, material);
  if (material instanceof THREE.MeshStandardMaterial) material.side = THREE.DoubleSide;
  return mesh;
}

/* ─────────────────────── archetype builders ─────────────────────── */

function buildWalker(
  s: number,
  colors: CreatureColors,
  traits: SpeciesTraits
): CreatureMesh {
  const root = new THREE.Group();

  const lengthMul = traits.lengthMul;
  const bodyR = 0.42 * s;
  const hipY = 1.0 * s;

  // Main body — clean ellipsoid stretched along forward axis.
  const body = ellipsoid(bodyR, 1.55 * lengthMul, 1.0, 1.05, colors.bodyMat, 1);
  body.position.y = hipY;
  root.add(body);

  // Belly accent — slightly lighter undertone using the accent colour.
  const belly = ellipsoid(bodyR * 0.78, 1.45 * lengthMul, 0.55, 0.95, colors.accentMat, 1);
  belly.position.y = hipY - bodyR * 0.5;
  root.add(belly);

  // Neck — tapered cylinder bent forward and up.
  const neckPivot = new THREE.Group();
  neckPivot.position.set(bodyR * 1.3 * lengthMul, hipY + bodyR * 0.4, 0);
  neckPivot.rotation.z = -0.55;
  const neck = tapered(0.16 * s, 0.18 * s, 0.55 * s, colors.bodyMat, 8);
  neck.position.y = 0.27 * s;
  neckPivot.add(neck);
  root.add(neckPivot);

  // Head — rounded ellipsoid with snout.
  const headGroup = new THREE.Group();
  const headR = 0.22 * s;
  const head = ellipsoid(headR, 1.2, 0.95, 1.0, colors.bodyMat, 1);
  headGroup.add(head);
  // Snout — accent-coloured nose stub.
  const snout = ellipsoid(0.13 * s, 1.4, 0.85, 0.85, colors.accentMat, 1);
  snout.position.set(headR * 1.05, -headR * 0.18, 0);
  headGroup.add(snout);
  // Ears — symmetric cones tilted out and back.
  const earL = buildEar(0.16 * s, colors.accentMat);
  earL.position.set(-headR * 0.1, headR * 1.05, headR * 0.55);
  earL.rotation.set(0.0, 0.0, -0.15);
  headGroup.add(earL);
  const earR = earL.clone();
  earR.position.z = -headR * 0.55;
  headGroup.add(earR);
  // Eyes.
  const eyeL = buildEye(0.05 * s, colors.emissiveMat, colors.accentMat);
  eyeL.position.set(headR * 0.65, headR * 0.05, headR * 0.55);
  headGroup.add(eyeL);
  const eyeR = eyeL.clone();
  eyeR.position.z = -headR * 0.55;
  headGroup.add(eyeR);

  // Position the head at the end of the bent neck (approx).
  headGroup.position.set(
    bodyR * 1.3 * lengthMul + Math.sin(0.55) * 0.55 * s,
    hipY + bodyR * 0.4 + Math.cos(0.55) * 0.55 * s,
    0
  );
  root.add(headGroup);

  // Legs — diagonal-trot gait. Length matches hipY exactly so feet rest
  // at the creature origin (= ground level when sampled).
  const legLength = hipY;
  const hipR = (traits.heavyLegs ? 0.13 : 0.1) * s;
  const footR = (traits.heavyLegs ? 0.1 : 0.075) * s;
  const legs: THREE.Group[] = [];
  // [forwardOffset, sideOffset, gaitPhase]. Diagonal pairs share phase.
  const legSpec: ReadonlyArray<readonly [number, number, number]> = [
    [0.42, 0.3, 0],            // right-front
    [0.42, -0.3, Math.PI],     // left-front
    [-0.4, 0.3, Math.PI],      // right-back (diagonal of left-front)
    [-0.4, -0.3, 0]            // left-back  (diagonal of right-front)
  ];
  for (const [fx, sz, phase] of legSpec) {
    const leg = buildLeg(legLength, hipR, footR, colors.bodyMat);
    leg.position.set(fx * lengthMul * s, hipY, sz * s);
    leg.userData['gaitPhase'] = phase;
    root.add(leg);
    legs.push(leg);
  }

  // Tail — short tapered cylinder pointing back.
  const tailPivot = new THREE.Group();
  tailPivot.position.set(-bodyR * 1.25 * lengthMul, hipY + bodyR * 0.25, 0);
  tailPivot.rotation.z = 1.4;
  const tail = tapered(0.075 * s, 0.04 * s, 0.5 * s, colors.accentMat, 6);
  tail.position.y = 0.25 * s;
  tailPivot.add(tail);
  root.add(tailPivot);

  // Per-species accents.
  if (traits.crystalPlates) addCrystalSpikes(root, s, hipY, colors);
  if (traits.metalPlates) addArmorPlates(root, s, hipY, colors, bodyR * 1.55 * lengthMul);

  return {
    root,
    parts: { body, head: headGroup, legs, wings: [], tail: tailPivot },
    bodyRadius: bodyR * 1.2,
    bodyHeight: hipY + bodyR
  };
}

function buildCrawler(
  s: number,
  colors: CreatureColors,
  traits: SpeciesTraits
): CreatureMesh {
  const root = new THREE.Group();

  // Segmented body — clean spheres of decreasing size, tapered backward.
  const segCount = traits.elongated ? 5 : 4;
  const segments: THREE.Mesh[] = [];
  const segScales = traits.elongated
    ? [0.48, 0.44, 0.4, 0.34, 0.28]
    : [0.46, 0.42, 0.36, 0.3];
  const stride = 0.36;
  // Forward-most segment (head-end) at +X, tail at -X.
  for (let i = 0; i < segCount; i++) {
    const r = (segScales[i] ?? 0.3) * s;
    const seg = ellipsoid(
      r,
      1.05,
      0.85,
      1.0,
      i % 2 === 1 ? colors.accentMat : colors.bodyMat,
      1
    );
    const xOff = ((segCount - 1 - i) - (segCount - 1) * 0.5) * stride * s;
    seg.position.set(xOff, r * 0.85, 0);
    seg.userData['baseY'] = r * 0.85;
    seg.userData['waveIndex'] = i;
    root.add(seg);
    segments.push(seg);
  }

  // Head bulb at the front.
  const headR = 0.32 * s;
  const headGroup = new THREE.Group();
  const head = ellipsoid(headR, 1.1, 0.9, 1.05, colors.bodyMat, 1);
  headGroup.add(head);
  // Eyes on stalks.
  const stalkMat = colors.bodyMat;
  for (const sign of [1, -1]) {
    const stalk = tapered(0.02 * s, 0.025 * s, 0.18 * s, stalkMat, 5);
    stalk.position.set(headR * 0.45, headR * 0.5, sign * headR * 0.45);
    stalk.rotation.x = sign * 0.25;
    headGroup.add(stalk);
    const eyeBall = buildEye(0.05 * s, colors.emissiveMat, colors.accentMat);
    eyeBall.position.set(headR * 0.45, headR * 0.5 + 0.18 * s, sign * headR * 0.45 + sign * 0.05 * s);
    headGroup.add(eyeBall);
  }
  // Mouth/snout dent.
  const snout = ellipsoid(headR * 0.38, 0.9, 0.7, 1.0, colors.accentMat, 1);
  snout.position.set(headR * 0.85, -headR * 0.15, 0);
  headGroup.add(snout);

  // Position head ahead of the front segment.
  const frontSegX = ((segCount - 1) - (segCount - 1) * 0.5) * stride * s;
  headGroup.position.set(frontSegX + 0.32 * s, headR * 0.95, 0);
  root.add(headGroup);

  // Per-species accents.
  if (traits.metalPlates) addArmorPlates(root, s, 0.5 * s, colors, segCount * stride * s);
  if (traits.glowPatches && colors.emissiveMat) {
    addGlowPatches(root, s, colors.emissiveMat, frontSegX, segCount * stride * s);
  }
  if (traits.crystalPlates) {
    // Small crystal nubs along the spine.
    for (let i = 0; i < segCount - 1; i++) {
      const xOff = (i - segCount * 0.5 + 0.5) * stride * s;
      const nub = cone(0.06 * s, 0.16 * s, colors.emissiveMat ?? colors.accentMat, 5);
      nub.position.set(xOff, 0.55 * s, 0);
      root.add(nub);
    }
  }

  return {
    root,
    parts: {
      body: segments[0] ?? root,
      head: headGroup,
      legs: [],
      wings: [],
      segments
    },
    bodyRadius: 0.4 * s,
    bodyHeight: 0.7 * s
  };
}

function buildGlider(
  s: number,
  colors: CreatureColors,
  traits: SpeciesTraits
): CreatureMesh {
  const root = new THREE.Group();

  // Streamlined torpedo body.
  const body = ellipsoid(0.32 * s, 1.7, 0.72, 0.95, colors.bodyMat, 1);
  root.add(body);

  // Belly — accent lower half.
  const belly = ellipsoid(0.28 * s, 1.55, 0.45, 0.8, colors.accentMat, 1);
  belly.position.y = -0.06 * s;
  root.add(belly);

  // Optional bulbous abdomen at the tail end (polinizador, coletor).
  if (traits.bulbAbdomen) {
    const abdomen = ellipsoid(0.28 * s, 1.05, 1.0, 0.95, colors.accentMat, 1);
    abdomen.position.set(-0.55 * s, 0, 0);
    root.add(abdomen);
  } else {
    const tailFin = ellipsoid(0.18 * s, 0.85, 0.55, 1.45, colors.accentMat, 1);
    tailFin.position.set(-0.55 * s, 0, 0);
    root.add(tailFin);
  }

  // Head bulb at the front.
  const headGroup = new THREE.Group();
  const headR = 0.18 * s;
  const head = ellipsoid(headR, 1.1, 0.95, 1.05, colors.bodyMat, 1);
  headGroup.add(head);
  // Eyes (front-side, larger for visibility).
  const eyeL = buildEye(0.045 * s, colors.emissiveMat, colors.accentMat);
  eyeL.position.set(headR * 0.6, headR * 0.1, headR * 0.55);
  headGroup.add(eyeL);
  const eyeR = eyeL.clone();
  eyeR.position.z = -headR * 0.55;
  headGroup.add(eyeR);
  headGroup.position.set(0.58 * s, 0.04 * s, 0);
  root.add(headGroup);

  // Wings — soft membranes or crystal blades.
  let wingLength = 1.55 * s;
  let wingWidth = 0.55 * s;
  if (traits.slimWings) {
    wingLength = 1.35 * s;
    wingWidth = 0.32 * s;
  }
  const wingMat = colors.accentMat;
  wingMat.side = THREE.DoubleSide;
  const buildOne = traits.crystalWings
    ? () => buildCrystalWing(wingLength, wingWidth, wingMat)
    : () => buildSoftWing(wingLength, wingWidth, wingMat);

  const wL = buildOne();
  wL.rotation.z = Math.PI / 2;
  wL.rotation.y = -Math.PI / 2;
  wL.position.set(0, 0.05 * s, 0.42 * s);
  root.add(wL);
  const wR = buildOne();
  wR.rotation.z = -Math.PI / 2;
  wR.rotation.y = -Math.PI / 2;
  wR.position.set(0, 0.05 * s, -0.42 * s);
  root.add(wR);

  // Glow underbelly for emissive species.
  if (colors.emissiveMat) {
    const glow = ellipsoid(0.45 * s, 1.4, 0.4, 0.7, colors.emissiveMat, 1);
    glow.position.y = -0.18 * s;
    root.add(glow);
  }

  if (traits.trailingTendrils) {
    addTendrils(root, s, colors);
  }

  return {
    root,
    parts: { body, head: headGroup, legs: [], wings: [wL, wR] },
    bodyRadius: 0.42 * s,
    bodyHeight: 0.7 * s
  };
}

function buildPredator(
  s: number,
  colors: CreatureColors,
  traits: SpeciesTraits
): CreatureMesh {
  const root = new THREE.Group();

  const lengthMul = traits.lengthMul;
  const bodyR = 0.4 * s;
  const hipY = 0.92 * s;

  // Lean elongated body.
  const body = ellipsoid(bodyR, 1.85 * lengthMul, 0.85, 0.95, colors.bodyMat, 1);
  body.position.y = hipY;
  root.add(body);

  // Belly accent.
  const belly = ellipsoid(bodyR * 0.78, 1.7 * lengthMul, 0.42, 0.85, colors.accentMat, 1);
  belly.position.y = hipY - bodyR * 0.45;
  root.add(belly);

  // Long predatory neck — straight angle, low bend.
  const neckPivot = new THREE.Group();
  neckPivot.position.set(bodyR * 1.5 * lengthMul, hipY + bodyR * 0.18, 0);
  neckPivot.rotation.z = -0.32;
  const neck = tapered(0.18 * s, 0.16 * s, 0.55 * s, colors.bodyMat, 8);
  neck.position.y = 0.27 * s;
  neckPivot.add(neck);
  root.add(neckPivot);

  // Predator head — wedge silhouette with sharp jaw.
  const headGroup = new THREE.Group();
  const headR = 0.24 * s;
  const head = ellipsoid(headR, 1.55, 0.85, 1.0, colors.bodyMat, 1);
  headGroup.add(head);
  // Lower jaw — slightly forward and below the head.
  const jaw = ellipsoid(headR * 0.85, 1.55, 0.42, 0.9, colors.accentMat, 1);
  jaw.position.set(headR * 0.18, -headR * 0.45, 0);
  headGroup.add(jaw);
  // Fang-like protrusions (small cones pointing down).
  for (const sign of [1, -1]) {
    const fang = cone(0.045 * s, 0.13 * s, colors.accentMat, 4);
    fang.rotation.x = Math.PI;
    fang.position.set(headR * 0.85, -headR * 0.5, sign * headR * 0.32);
    headGroup.add(fang);
  }
  // Glowing eyes — predators always emissive.
  const eyeMat = colors.emissiveMat ?? colors.accentMat;
  const eyeL = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.06 * s, 0),
    eyeMat
  );
  eyeL.position.set(headR * 0.7, headR * 0.18, headR * 0.55);
  headGroup.add(eyeL);
  const eyeR = eyeL.clone();
  eyeR.position.z = -headR * 0.55;
  headGroup.add(eyeR);

  // Position head at end of neck.
  headGroup.position.set(
    bodyR * 1.5 * lengthMul + Math.sin(0.32) * 0.55 * s,
    hipY + bodyR * 0.18 + Math.cos(0.32) * 0.55 * s,
    0
  );
  root.add(headGroup);

  // Six legs (predator) with diagonal-trot phasing.
  const legLength = hipY;
  const hipR = (traits.heavyLegs ? 0.12 : 0.095) * s;
  const footR = (traits.heavyLegs ? 0.09 : 0.07) * s;
  const legs: THREE.Group[] = [];
  // Six-leg trot: front/back share a phase, mid alternates.
  const legSpec: ReadonlyArray<readonly [number, number, number]> = [
    [0.55, 0.28, 0],
    [0.55, -0.28, Math.PI],
    [0.0, 0.32, Math.PI],
    [0.0, -0.32, 0],
    [-0.55, 0.28, Math.PI],
    [-0.55, -0.28, 0]
  ];
  for (const [fx, sz, phase] of legSpec) {
    const leg = buildLeg(legLength, hipR, footR, colors.bodyMat);
    leg.position.set(fx * lengthMul * s, hipY, sz * s);
    leg.userData['gaitPhase'] = phase;
    root.add(leg);
    legs.push(leg);
  }

  // Long whip tail.
  const tailPivot = new THREE.Group();
  tailPivot.position.set(-bodyR * 1.55 * lengthMul, hipY + bodyR * 0.1, 0);
  tailPivot.rotation.z = 0.95;
  const tail = tapered(0.09 * s, 0.025 * s, 0.95 * s * lengthMul, colors.accentMat, 6);
  tail.position.y = 0.5 * s * lengthMul;
  tailPivot.add(tail);
  root.add(tailPivot);

  // Per-species accents.
  if (traits.rings) addRingSegments(root, s, hipY, colors, bodyR * 1.85 * lengthMul);
  if (traits.dorsalGlow && colors.emissiveMat) {
    addDorsalVents(root, s, hipY, bodyR, colors.emissiveMat);
  } else {
    addDorsalRidge(root, s, hipY, bodyR, colors.accentMat);
  }
  if (traits.metalPlates) addArmorPlates(root, s, hipY, colors, bodyR * 1.85 * lengthMul);

  return {
    root,
    parts: { body, head: headGroup, legs, wings: [], tail: tailPivot },
    bodyRadius: bodyR * 1.3,
    bodyHeight: hipY + bodyR
  };
}

function buildSpectre(
  s: number,
  colors: CreatureColors,
  traits: SpeciesTraits
): CreatureMesh {
  void traits;
  const root = new THREE.Group();

  // Glowing core orb.
  const main = ellipsoid(0.65 * s, 1.05, 1.35, 1.05, colors.bodyMat, 1);
  root.add(main);

  // Halo around the core (additive-friendly emissive material).
  if (colors.emissiveMat) {
    const halo = ellipsoid(0.92 * s, 1.15, 1.35, 1.15, colors.emissiveMat, 1);
    halo.renderOrder = -1;
    root.add(halo);
  }

  // Trailing tendrils underneath.
  const tendrils: THREE.Mesh[] = [];
  const count = 7;
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2;
    const t = tapered(0.05 * s, 0.02 * s, 0.7 * s, colors.accentMat, 6);
    t.rotation.z = Math.PI / 2;
    t.rotation.y = a;
    t.position.set(Math.cos(a) * 0.18 * s, -0.62 * s, Math.sin(a) * 0.18 * s);
    root.add(t);
    tendrils.push(t);
  }

  return {
    root,
    parts: { body: main, head: undefined, legs: tendrils, wings: [] },
    bodyRadius: 0.85 * s,
    bodyHeight: 1.4 * s
  };
}

/* ─────────────────────── species accents ─────────────────────── */

function addRingSegments(
  root: THREE.Group,
  s: number,
  hipY: number,
  colors: CreatureColors,
  bodyLength: number
): void {
  const positions = [-bodyLength * 0.28, 0, bodyLength * 0.28];
  for (const xp of positions) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.55 * s, 0.07 * s, 6, 18),
      colors.accentMat
    );
    ring.position.set(xp, hipY, 0);
    ring.rotation.y = Math.PI / 2;
    root.add(ring);
  }
}

function addCrystalSpikes(
  root: THREE.Group,
  s: number,
  hipY: number,
  colors: CreatureColors
): void {
  const mat = colors.emissiveMat ?? colors.accentMat;
  const positions = [-0.45, -0.18, 0.05, 0.32];
  for (const xp of positions) {
    const spike = cone(0.07 * s, 0.32 * s, mat, 5);
    spike.position.set(xp * s, hipY + 0.5 * s, 0);
    spike.rotation.z = (Math.random() - 0.5) * 0.12;
    root.add(spike);
  }
}

function addArmorPlates(
  root: THREE.Group,
  s: number,
  hipY: number,
  colors: CreatureColors,
  bodyLength: number
): void {
  for (let i = 0; i < 4; i++) {
    const t = (i / 3) - 0.5;
    const plate = ellipsoid(0.18 * s, 1.6, 0.32, 1.05, colors.accentMat, 1);
    plate.position.set(t * bodyLength * 0.6, hipY + 0.32 * s, 0);
    root.add(plate);
  }
}

function addDorsalVents(
  root: THREE.Group,
  s: number,
  hipY: number,
  bodyR: number,
  emissiveMat: THREE.MeshStandardMaterial
): void {
  for (let i = 0; i < 5; i++) {
    const t = (i / 4) - 0.5;
    const vent = ellipsoid(0.1 * s, 1.0, 1.6, 0.6, emissiveMat, 1);
    vent.position.set(t * 1.5 * s, hipY + bodyR * 0.95, 0);
    root.add(vent);
  }
}

function addDorsalRidge(
  root: THREE.Group,
  s: number,
  hipY: number,
  bodyR: number,
  accentMat: THREE.MeshStandardMaterial
): void {
  for (let i = 0; i < 4; i++) {
    const t = (i / 3) - 0.5;
    const fin = cone(0.07 * s, 0.16 * s, accentMat, 4);
    fin.position.set(t * 1.4 * s, hipY + bodyR * 0.95, 0);
    root.add(fin);
  }
}

function addGlowPatches(
  root: THREE.Group,
  s: number,
  emissiveMat: THREE.MeshStandardMaterial,
  frontSegX: number,
  bodySpan: number
): void {
  for (let i = 0; i < 5; i++) {
    const t = (i / 4) - 0.5;
    const patch = ellipsoid(0.08 * s, 1.2, 0.5, 1.2, emissiveMat, 1);
    patch.position.set(frontSegX + t * bodySpan * 0.7, 0.55 * s, (i % 2 === 0 ? 0.18 : -0.18) * s);
    root.add(patch);
  }
}

function addTendrils(root: THREE.Group, s: number, colors: CreatureColors): void {
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    const t = tapered(0.04 * s, 0.02 * s, 0.55 * s, colors.accentMat, 5);
    t.rotation.z = Math.PI / 2;
    t.rotation.y = a;
    t.position.set(Math.cos(a) * 0.18 * s, -0.45 * s, Math.sin(a) * 0.18 * s);
    root.add(t);
  }
}
